import React, { useState, useEffect } from 'react';
import { Layout } from "./components/dashboard/layout";

const DynamicAttendanceDashboard = () => {
  const [gradeData, setGradeData] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [deviceAnalytics, setDeviceAnalytics] = useState([]);
  const [hourlyAnalytics, setHourlyAnalytics] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  // API base URL - update this to match your server
  const API_BASE = 'https://raptornet.aischennai.org/screening-manager/gate-api/getComprehensiveSummary.php';

  const fetchEndpoint = async (endpoint, date) => {
    const response = await fetch(`${API_BASE}?endpoint=${endpoint}&date=${date}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
    }
    return response.json();
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all endpoints in parallel
      const [summary, stats, devices, hourly] = await Promise.all([
        fetchEndpoint('summary', selectedDate),
        fetchEndpoint('stats', selectedDate),
        fetchEndpoint('devices', selectedDate),
        fetchEndpoint('hourly', selectedDate)
      ]);

      // Ensure gradeData is always an array
      let processedGradeData = [];
      if (Array.isArray(summary)) {
        processedGradeData = summary;
      } else if (summary && typeof summary === 'object') {
        // If summary is an object, try to extract data array
        if (summary.data && Array.isArray(summary.data)) {
          processedGradeData = summary.data;
        } else if (summary.grades && Array.isArray(summary.grades)) {
          processedGradeData = summary.grades;
        } else {
          // Convert single object to array
          processedGradeData = [summary];
        }
      }

      // Ensure other data arrays are properly formatted
      const processedDevices = Array.isArray(devices) ? devices : 
        (devices && devices.data && Array.isArray(devices.data)) ? devices.data : [];
      
      const processedHourly = Array.isArray(hourly) ? hourly : 
        (hourly && hourly.data && Array.isArray(hourly.data)) ? hourly.data : [];

      setGradeData(processedGradeData);
      setOverallStats(stats.data || null);
      setDeviceAnalytics(processedDevices);
      setHourlyAnalytics(processedHourly);
      setLastUpdated(new Date());
      
      console.log('Fetched data:', {
        gradeData: processedGradeData,
        stats,
        devices: processedDevices,
        hourly: processedHourly
      });
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
      // Fallback to empty data
      setGradeData([]);
      setOverallStats(null);
      setDeviceAnalytics([]);
      setHourlyAnalytics([]);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotals = () => {
    if (overallStats) {
      // Use overall stats if available
      return {
        totalStudents: overallStats.total_students || 0,
        checkedIn: overallStats.total_checked_in || 0,
        checkedOut: overallStats.total_checked_out || 0,
        onCampus: overallStats.total_on_campus || 0,
        notCheckedIn: overallStats.total_not_checked_in || 0
      };
    }
    
    // Fallback to calculating from grade data - ensure gradeData is array
    if (!Array.isArray(gradeData)) {
      return { totalStudents: 0, checkedIn: 0, checkedOut: 0, onCampus: 0, notCheckedIn: 0 };
    }
    
    return gradeData.reduce((totals, grade) => ({
      totalStudents: totals.totalStudents + (grade.total_students || 0),
      checkedIn: totals.checkedIn + (grade.checked_in_students || 0),
      checkedOut: totals.checkedOut + (grade.checked_out_students || 0),
      onCampus: totals.onCampus + (grade.still_on_campus || 0),
      notCheckedIn: totals.notCheckedIn + (grade.not_checked_in || 0)
    }), { totalStudents: 0, checkedIn: 0, checkedOut: 0, onCampus: 0, notCheckedIn: 0 });
  };

  useEffect(() => {
    fetchAllData();
  }, [selectedDate]);

  const totals = calculateTotals();
  const overallAttendanceRate = totals.totalStudents > 0 ? 
    ((totals.checkedIn / totals.totalStudents) * 100).toFixed(1) : 0;

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '24px'
    },
    maxWidth: {
      margin: '0 auto'
    },
    headerCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: '24px',
      marginBottom: '24px'
    },
    headerFlex: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1a1a1a',
      margin: '0 0 8px 0'
    },
    subtitle: {
      color: '#666',
      margin: 0
    },
    controls: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    dateInput: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none'
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    buttonDisabled: {
      opacity: '0.5',
      cursor: 'not-allowed'
    },
    lastUpdated: {
      marginTop: '16px',
      fontSize: '14px',
      color: '#22c55e',
      fontWeight: '500'
    },
    errorMessage: {
      marginTop: '16px',
      padding: '12px',
      backgroundColor: '#fef2f2',
      color: '#dc2626',
      borderRadius: '8px',
      fontSize: '14px',
      border: '1px solid #fecaca'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    summaryCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: '24px',
      borderLeft: '4px solid'
    },
    cardFlex: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    cardText: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#666',
      margin: '0 0 4px 0'
    },
    cardNumber: {
      fontSize: '32px',
      fontWeight: 'bold',
      margin: 0
    },
    emoji: {
      fontSize: '40px'
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      marginBottom: '24px'
    },
    tableHeader: {
      padding: '24px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: 'white'
    },
    tableTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0 0 4px 0'
    },
    tableSubtitle: {
      fontSize: '14px',
      color: '#666',
      margin: 0
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      backgroundColor: '#f9fafb',
      padding: '12px 24px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '500',
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    thCenter: {
      textAlign: 'center'
    },
    td: {
      padding: '16px 24px',
      borderBottom: '1px solid #f3f4f6'
    },
    tdCenter: {
      textAlign: 'center'
    },
    gradeCell: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    gradeName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1a1a1a'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500'
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    progressBar: {
      width: '64px',
      height: '8px',
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#2563eb',
      borderRadius: '4px'
    },
    totalsRow: {
      backgroundColor: '#f9fafb',
      fontWeight: '600'
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px'
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: '4px solid #f3f4f6',
      borderTop: '4px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    emptyState: {
      textAlign: 'center',
      padding: '48px'
    },
    emptyIcon: {
      fontSize: '64px',
      marginBottom: '16px'
    },
    emptyText: {
      color: '#9ca3af'
    },
    analyticsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginTop: '24px'
    },
    deviceItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    deviceName: {
      fontSize: '14px',
      fontWeight: '500'
    },
    hourlyItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0'
    },
    hourText: {
      fontSize: '14px',
      fontWeight: '500'
    },
    hourCount: {
      fontSize: '14px',
      color: '#666'
    }
  };

  return (
    <Layout>
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media (min-width: 640px) {
            .header-flex {
              flex-direction: row !important;
              align-items: center !important;
            }
          }
        `}
      </style>
      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.headerCard}>
          <div style={styles.headerFlex} className="header-flex">
            <div>
              <h1 style={styles.title}>📚 Daily Attendance Summary</h1>
              <p style={styles.subtitle}>Real-time grade-wise attendance overview and statistics</p>
            </div>
            <div style={styles.controls}>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={styles.dateInput}
              />
              <button
                onClick={fetchAllData}
                disabled={isLoading}
                style={{
                  ...styles.button,
                  ...(isLoading ? styles.buttonDisabled : {})
                }}
              >
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
          {lastUpdated && (
            <div style={styles.lastUpdated}>
              Last updated: {lastUpdated.toLocaleString()}
            </div>
          )}
          {error && (
            <div style={styles.errorMessage}>
              Error: {error}. Please check your API connection and try again.
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <div style={{...styles.summaryCard, borderLeftColor: '#2563eb'}}>
            <div style={styles.cardFlex}>
              <div>
                <p style={styles.cardText}>Total Students</p>
                <p style={{...styles.cardNumber, color: '#2563eb'}}>{totals.totalStudents}</p>
              </div>
              <div style={styles.emoji}>👥</div>
            </div>
          </div>

          <div style={{...styles.summaryCard, borderLeftColor: '#16a34a'}}>
            <div style={styles.cardFlex}>
              <div>
                <p style={styles.cardText}>Checked In</p>
                <p style={{...styles.cardNumber, color: '#16a34a'}}>{totals.checkedIn}</p>
              </div>
              <div style={styles.emoji}>✅</div>
            </div>
          </div>

          <div style={{...styles.summaryCard, borderLeftColor: '#ea580c'}}>
            <div style={styles.cardFlex}>
              <div>
                <p style={styles.cardText}>On Campus</p>
                <p style={{...styles.cardNumber, color: '#ea580c'}}>{totals.onCampus}</p>
              </div>
              <div style={styles.emoji}>🏫</div>
            </div>
          </div>

          <div style={{...styles.summaryCard, borderLeftColor: '#dc2626'}}>
            <div style={styles.cardFlex}>
              <div>
                <p style={styles.cardText}>Checked Out</p>
                <p style={{...styles.cardNumber, color: '#dc2626'}}>{totals.checkedOut}</p>
              </div>
              <div style={styles.emoji}>🚪</div>
            </div>
          </div>

          <div style={{...styles.summaryCard, borderLeftColor: '#9333ea'}}>
            <div style={styles.cardFlex}>
              <div>
                <p style={styles.cardText}>Not Checked In</p>
                <p style={{...styles.cardNumber, color: '#9333ea'}}>{totals.notCheckedIn}</p>
              </div>
              <div style={styles.emoji}>📊</div>
            </div>
          </div>

          <div style={{...styles.summaryCard, borderLeftColor: '#9333ea'}}>
            <div style={styles.cardFlex}>
              <div>
                <p style={styles.cardText}>Attendance Rate</p>
                <p style={{...styles.cardNumber, color: '#9333ea'}}>{overallAttendanceRate}%</p>
              </div>
              <div style={styles.emoji}>📊</div>
            </div>
          </div>
        </div>

        {/* Grade-wise Table */}
        <div style={styles.tableContainer}>
          <div style={styles.tableHeader}>
            <h2 style={styles.tableTitle}>Grade-wise Breakdown</h2>
            <p style={styles.tableSubtitle}>Detailed attendance statistics by grade level</p>
          </div>

          {isLoading ? (
            <div style={styles.loading}>
              <div style={styles.spinner}></div>
            </div>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Grade</th>
                    <th style={{...styles.th, ...styles.thCenter}}>Total Students</th>
                    <th style={{...styles.th, ...styles.thCenter}}>Checked In</th>
                    <th style={{...styles.th, ...styles.thCenter}}>On Campus</th>
                    <th style={{...styles.th, ...styles.thCenter}}>Checked Out</th>
                    <th style={{...styles.th, ...styles.thCenter}}>Not Checked In</th>
                    <th style={{...styles.th, ...styles.thCenter}}>Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(gradeData) && gradeData.map((grade, index) => {
                    const attendanceRate = grade.total_students > 0 ? 
                      ((grade.checked_in_students / grade.total_students) * 100).toFixed(1) : 0;
                    
                    return (
                      <tr key={index} style={{backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'}}>
                        <td style={styles.td}>
                          <div style={styles.gradeCell}>
                            <span>🎓</span>
                            <span style={styles.gradeName}>{grade.current_grade || `Grade ${index + 1}`}</span>
                          </div>
                        </td>
                        <td style={{...styles.td, ...styles.tdCenter}}>
                          <span style={{fontSize: '14px', fontWeight: '600'}}>{grade.total_students || 0}</span>
                        </td>
                        <td style={{...styles.td, ...styles.tdCenter}}>
                          <span style={{...styles.badge, backgroundColor: '#dcfce7', color: '#166534'}}>
                            {grade.checked_in_students || 0}
                          </span>
                        </td>
                        <td style={{...styles.td, ...styles.tdCenter}}>
                          <span style={{...styles.badge, backgroundColor: '#fed7aa', color: '#9a3412'}}>
                            {grade.still_on_campus || 0}
                          </span>
                        </td>
                        <td style={{...styles.td, ...styles.tdCenter}}>
                          <span style={{...styles.badge, backgroundColor: '#fecaca', color: '#991b1b'}}>
                            {grade.checked_out_students || 0}
                          </span>
                        </td>
                        <td style={{...styles.td, ...styles.tdCenter}}>
                          <span style={{...styles.badge, backgroundColor: '#f3f4f6', color: '#374151'}}>
                            {grade.not_checked_in || 0}
                          </span>
                        </td>
                        <td style={{...styles.td, ...styles.tdCenter}}>
                          <div style={styles.progressContainer}>
                            <span style={{fontSize: '14px', fontWeight: '500'}}>{attendanceRate}%</span>
                            <div style={styles.progressBar}>
                              <div
                                style={{
                                  ...styles.progressFill,
                                  width: `${attendanceRate}%`
                                }}
                              ></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Totals Row */}
                  {Array.isArray(gradeData) && gradeData.length > 0 && (
                    <tr style={styles.totalsRow}>
                      <td style={styles.td}>
                        <span style={{fontSize: '14px', fontWeight: 'bold'}}>TOTALS</span>
                      </td>
                      <td style={{...styles.td, ...styles.tdCenter}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold'}}>{totals.totalStudents}</span>
                      </td>
                      <td style={{...styles.td, ...styles.tdCenter}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold', color: '#166534'}}>{totals.checkedIn}</span>
                      </td>
                      <td style={{...styles.td, ...styles.tdCenter}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold', color: '#9a3412'}}>{totals.onCampus}</span>
                      </td>
                      <td style={{...styles.td, ...styles.tdCenter}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold', color: '#991b1b'}}>{totals.checkedOut}</span>
                      </td>
                      <td style={{...styles.td, ...styles.tdCenter}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold', color: '#374151'}}>{totals.notCheckedIn}</span>
                      </td>
                      <td style={{...styles.td, ...styles.tdCenter}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold', color: '#2563eb'}}>{overallAttendanceRate}%</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {(!Array.isArray(gradeData) || gradeData.length === 0) && !isLoading && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📊</div>
              <p style={styles.emptyText}>No attendance data available for the selected date.</p>
            </div>
          )}
        </div>

        {/* Analytics Grid */}
        
      </div>
    </div>
    </Layout>
  );
};

export default DynamicAttendanceDashboard;